import { connect } from 'pwa-helpers';
import store, { waitForUser } from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { customElement, state } from 'lit/decorators.js';
import { css, html, nothing } from 'lit';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { StoreState } from '../../../store/store.interface';
import { getWishListsData } from '../store/wish-list.selector';
import { WishList, WishLists } from '../interfaces/wish-list-items.interface';
import {
  _renderLoadingTemplate,
  getSharedWishListUrl,
  getWishlistShareLinkOffCanvasTitle,
  isWishListsFailure,
  isWishListsLoadingOrIdle,
  isWishListsSuccess,
} from '../utils/wish-list.util';
import { loadCustomer } from '../../customer/customer';
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { UsePaginationInMemory } from '../../pagination/in-memory/use-pagination-in-memory.interface';
import { getPaginationDetails, getPaginationMessage } from '../../pagination/utils/pagination.utils';
import { ErrorToastWishList } from '../constants/wish-list-error-toast-status';
import { WishListDropdownConstants } from '../constants/wish-list-dropdown-constants';
import {
  openCreateWishListOffCanvas,
  openDeleteWishListOffCanvas,
  openShareWishListOffCanvas,
  openUpdateWishListOffCanvas,
} from '../utils/wish-list-offcanvas.util';
import { renderErrorToast } from '../utils/wish-list-toast.util';
import '../components/wish-list-card-display';
import '../../off-canvas/off-canvas';
import '../../toast/toast';
import '../../pagination/in-memory/pagination-in-memory';
import './wish-list-create-off-canvas';
import './wish-list-delete-off-canvas';
import './wish-list-update-off-canvas';
import { getShareLinkOffCanvasContent } from '../../off-canvas/common-off-canvases/utils/common-off-canvas.util';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { selectUser } from '../../user-profile/user-profile.selectors';

const MAX_ITEMS_PAGE = 10;

@customElement('mhecomm-my-account-wish-list')
export class MyAccountWistList extends connect(store)(BaseComponent) implements UsePaginationInMemory {
  static styles = [
    css`
      .loading-container {
        display: grid;
        grid-template-columns: 2fr 8fr 2fr;
        gap: 10px;
      }
    `,
  ];

  /**
   * model to load the wishlists data for the user
   */
  @state()
  private wishLists?: WishList[];

  @state()
  wishListsLoadingOrIdle = false;

  @state()
  wishListsSuccess = false;

  @state()
  wishListsFetchCallFailed = false;

  @state()
  private wishListItemsCount = 0;

  @state()
  private currentWishList?: WishList = undefined;

  @state()
  private currentPage = 0;
  private firstOfPagination = 1;
  private lastOfPagination = 1;

  @state()
  private user: UserProfile | undefined;

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  async connectedCallback() {
    super.connectedCallback();
    this.shadowRoot?.addEventListener('go-to-page-in-memory', (e: CustomEvent) =>
      this.updatePaginationData(e.detail.paginationNumber)
    );
    store.dispatch(fetchUserProfile());
    store.dispatch(loadCustomer());
    this.getWishListApiResponse();
    this.addEventListener('click', this.onWishlistOptionTrigger);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onWishlistOptionTrigger);
  }

  getWishListApiResponse() {
    waitForUser().then(() => {
      store.dispatch(fetchAllWishLists());
    });
  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);

    await store.dispatch(fetchAllWishLists()).then(() => {
      const wishLists: WishLists | undefined = getWishListsData(store.getState() as StoreState);
      wishLists && wishLists?.items?.length && this.wishListAnalytics.wishLists(this, wishLists, this.user!);
    });
  }

  /**
   * Make the redux call to load the wishlists data
   */
  stateChanged(state: StoreState) {
    super.stateChanged(state);
    this.user = selectUser(state);
    this.wishListsLoadingOrIdle = !this.wishLists && isWishListsLoadingOrIdle();
    this.wishListsSuccess = isWishListsSuccess();
    this.wishListsFetchCallFailed = isWishListsFailure();

    if (this.wishListsSuccess) {
      const wishLists: WishLists | undefined = getWishListsData(state);
      this.wishLists = wishLists?.items ?? [];
      this.wishListItemsCount = wishLists?.total_count ?? 0;
      this.hasWishLists() && this.updatePaginationData(this.currentPage);
    }
  }

  renderComp() {
    return html` <div class="mb-4">
        ${this.renderCreateWishListButton()}
      </div>
      ${this.renderWishListOffCanvas()}
      ${this.wishListsLoadingOrIdle
        ? _renderLoadingTemplate()
        : this.hasWishLists()
        ? this._renderWishLists()
        : this.wishListsFetchCallFailed
        ? renderErrorToast({
            error: ErrorToastWishList.FETCHING_WISH_LISTS,
            displayLoadingTemplate: true,
            autoHide: false,
            hideClose: true,
          })
        : this._renderNoContentTemplate()}`;
  }

  paginationMsg() {
    return html`
      <mhecomm-pagination-message
        ?useRawMessage=${true}
        message="${getPaginationMessage(this.currentPage, MAX_ITEMS_PAGE, this.wishListItemsCount)}"
        id="pagination_message"
        data-automation-id="pagination_message"
      >
      </mhecomm-pagination-message>
    `;
  }

  /**
   * render if no data is present
   */
  _renderNoContentTemplate() {
    return html`
      <div class="mt-5">
        <img
          src="/uxt/static/external/misc/no-wish-list.svg"
          class=" w-15 h-15"
          alt="No-Wishlists"
          aria-label="No-Wishlists"
        />
        <h1 aria-label="You don't have any lists yet.">You don't have any lists yet.</h1>
        <p aria-label="Start creating lists to save products you want to reference again later.">
          Start creating lists to save products you want to reference again later.
        </p>
      </div>
    `;
  }

  /**
   * render the wishlists data
   */
  _renderWishLists() {
    if (!this.hasWishLists()) {
      return nothing;
    }

    return html`
      ${this.paginationMsg()}
      ${this.getItemsToRender().map(
        (listToRender) =>
          html` <div id=${`wishlist-${listToRender.wishlist_id}`}>
            ${this.renderWishlistCardDisplay(listToRender)}
          </div>`
      )}
      ${this.renderPaginationInMemory()}
    `;
  }

  hasWishLists() {
    return this.wishLists && this.wishLists.length > 0;
  }

  getItemsToRender(): WishList[] {
    const newList: WishList[] = [];

    for (let i = this.firstOfPagination - 1; i < this.lastOfPagination; i++) {
      newList.push(this.wishLists![i]);
    }

    return newList;
  }

  /**
   * It should be used when the pagination ocurrs on the pagination-in-memory
   * or when some wishlist is removed, because we might need to update the results
   * based on the new total wish lists
   * @param paginationNumber - The page to navigate for or the current page if
   * a wish list was removed
   */
  updatePaginationData(paginationNumber: number) {
    const page = paginationNumber ? paginationNumber - 1 : this.currentPage;
    const { currentPage, firstOfPage, lastOfPage } = getPaginationDetails(
      page,
      MAX_ITEMS_PAGE,
      this.wishListItemsCount
    );
    this.firstOfPagination = firstOfPage;
    this.lastOfPagination = lastOfPage;
    this.currentPage = currentPage;
  }

  renderPaginationInMemory() {
    return html`
      <mhecomm-pagination-in-memory
        .currentPage=${this.currentPage}
        maxPagesShown="3"
        .total=${this.wishListItemsCount}
        .pageLength=${MAX_ITEMS_PAGE}
      ></mhecomm-pagination-in-memory>
    `;
  }

  renderWishlistCardDisplay(wishList: WishList) {
    return html` <mhecomm-wish-list-card-display .wishList=${wishList}></mhecomm-wish-list-card-display>`;
  }

  renderCreateWishListButton() {
    return html` <button
      id="create-wish-list-button"
      aria-label="Create a New List (opens in a modal)"
      class="btn btn-primary"
      type="button"
      @click=${() => openCreateWishListOffCanvas()}
    >
      + Create a New List
    </button>`;
  }

  renderWishListOffCanvas() {
    return html`
      <div>
        <mhecomm-wish-list-create-off-canvas></mhecomm-wish-list-create-off-canvas>
      </div>
      <div>
        <mhecomm-wish-list-update-off-canvas
          wishListId="${this.currentWishList?.wishlist_id!}"
          wishListName="${this.currentWishList?.name!}"
        ></mhecomm-wish-list-update-off-canvas>
      </div>
      <div>
        <mhecomm-wish-list-delete-off-canvas
          wishListId="${this.currentWishList?.wishlist_id!}"
          wishListName="${this.currentWishList?.name!}"
        ></mhecomm-wish-list-delete-off-canvas>
      </div>
      <div>
        ${getShareLinkOffCanvasContent(
          getWishlistShareLinkOffCanvasTitle(this.currentWishList?.name ?? ''),
          getSharedWishListUrl(this.currentWishList?.sharing_code ?? ''),
          false,
          false,
          () => this.wishListAnalytics.shareWishList(
            this, 
            this.currentWishList?.wishlist_id!, 
            this.currentWishList?.name!, 
            getSharedWishListUrl(this.currentWishList?.sharing_code ?? ''), 
            this.user!
          )!
        )}
      </div>
    `;
  }

  private onWishlistOptionTrigger(event: CustomEventInit): void {
    const eventName = event?.detail?.eventName;
    if (eventName !== 'wish-list-option-selection') {
      return;
    }

    const wishList = event?.detail?.wishList;
    if (!wishList) {
      console.error('Wish list not found, the action could not be performed.');
    }

    this.currentWishList = wishList;

    switch (event?.detail?.option) {
      case WishListDropdownConstants.EDIT_NAME:
        openUpdateWishListOffCanvas(this.currentWishList!);
        break;
      case WishListDropdownConstants.DELETE:
        openDeleteWishListOffCanvas(this.currentWishList?.wishlist_id!);
        break;
      case WishListDropdownConstants.SHARE:
        openShareWishListOffCanvas(this.currentWishList?.wishlist_id!);
        break;
      default:
        console.error('Wish list action not supported.');
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-account-wish-list': MyAccountWistList;
  }
}
